import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import {
  AGENT_MODEL,
  AGENT_TYPE,
  ApiService,
  Device,
  DialogService,
  LmapService,
  SchedulerFormat,
  TestAssembleService,
  TestLayerValue,
  TestMode,
  TestServerConfigService
} from '@nrwl/common-lib';
import { BaseSetupService, BaseSetupTab, setupConfig } from '@nrwl/test-setup';
import { Subject } from 'rxjs';
import {
  RFC2544ConfigFieldMapping as ConfigFieldMapping,
  IPAddressValue,
  vlanConfigValue
} from './models/models';

@Injectable({
  providedIn: 'root'
})
export class RFC2544SetupService extends BaseSetupService {
  tabDataChangeEvents = new Subject<any>();
  agentChangeEvents = new Subject<any>();
  testTaskName = 'rfc2544';
  deviceModel = 'QT600-10';
  selectedOriginator: Device;
  selectedDestination: Device;
  lmapService: LmapService;
  testAssembleService: TestAssembleService;
  serverConfigService: TestServerConfigService;

  constructor(
    apiService: ApiService,
    dialogService: DialogService,
    dialog: MatDialog,
    lmapService: LmapService,
    serverConfigService: TestServerConfigService,
    testAssmebleService: TestAssembleService
  ) {
    super(apiService, dialogService, dialog);
    this.lmapService = lmapService;
    this.testAssembleService = testAssmebleService;
    this.serverConfigService = serverConfigService;
  }
  openTestSetup(
    setupId: string,
    setupConfig: setupConfig,
    defaultParamsById = {},
    onSuccess = () => {},
    onFail = () => {},
    agentBasedConfig: any
  ): void {
    super.openTestSetup(
      setupId,
      setupConfig,
      defaultParamsById,
      onSuccess,
      onFail,
      agentBasedConfig
    );
  }

  getScheduleTimeStamp(timestamp: string): string {
    if (timestamp.toLowerCase() === 'now') {
      return null; //To start the test immediately -Need to update based on backend value
    } else {
      const currentTimestamp = new Date().getTime();
      if (Number(timestamp) < currentTimestamp) {
        return null;
      } else {
        return new Date(Number(timestamp)).toISOString();
      }
    }
  }

  getTestOptionsForSaveTemplate(): any {
    const testOptions = {};
    const listOfOptions = this.getOptions();
    listOfOptions.forEach(opt => {
      testOptions[opt.id] = opt.value;
    });
    return testOptions;
  }

  getAllTestParams(): any {
    this.output['config'] = this.getRfc2544TestConfig();
    return this._getFormatOutput();
  }

  getRfc2544TestConfig(): any {
    const testconfig = {};
    if (!this.output[ConfigFieldMapping.SUPPRESS_LOOP_COMMANDS]) {
      testconfig['loopback-device'] = 'QT600';
    } else {
      testconfig['loopback-device'] = 'other';
    }
    testconfig['swapped-addresses'] = 'true';
    testconfig['unit-convention'] = this.output[ConfigFieldMapping.STREAM_RATE_CONVENTION];
    testconfig['remote-vlan-tag-count'] =
      this.output[ConfigFieldMapping.REMOTE_VLAN_TAG].toString();
    testconfig['network-overhead'] = this.output[ConfigFieldMapping.NETWORK_OVERHEAD].toString();
    if (this.output[ConfigFieldMapping.LAYER_TO_TEST] === TestLayerValue.L2) {
      testconfig['layer2'] = this.getLayer2Details();
    }
    if (this.output[ConfigFieldMapping.LAYER_TO_TEST] === TestLayerValue.L3) {
      if (this.output[ConfigFieldMapping.IP_ADDRESS_TYPE] === IPAddressValue.IP_V4) {
        testconfig['layer3-ipv4'] = this.getLayer3Details();
      }
      if (this.output[ConfigFieldMapping.IP_ADDRESS_TYPE] === IPAddressValue.IP_V6) {
        testconfig['layer3-ipv6'] = this.getLayer3Details();
      }
    }
    testconfig['cir'] = this.output[ConfigFieldMapping.STREAM]['cir-from-engine-a'] * 1000;
    testconfig['test-timeout'] = this.output[ConfigFieldMapping.MAX_TEST_DURATION];
    testconfig['frame-size-info'] = this.getFrameListDetails();
    if (this.output[ConfigFieldMapping.THROUGHPUT]) {
      testconfig['throughput'] = this.getThroughputDetails();
    }
    if (this.output[ConfigFieldMapping.LATENCY]) {
      testconfig['latency-fdv'] = this.getLatencyDetails();
    }
    if (this.output[ConfigFieldMapping.FRAME_LOSS]) {
      testconfig['frameloss'] = this.getFrameLossDetails();
    }
    if (this.output[ConfigFieldMapping.BACK_TO_BACK]) {
      testconfig['bb-cbs'] = this.getB2bOrCbsDetails();
    }
    return testconfig;
  }

  createLMAPObject(dataModel: any): any {
    const testPointA = this.output[ConfigFieldMapping.SELECTED_ORIGINATOR]?.deviceData;
    const testPointB = this.output[ConfigFieldMapping.SELECTED_DESTINATION]?.deviceData;
    testPointA['id'] = testPointA.uniqueId;
    if (testPointB) {
      testPointB['id'] = testPointB.uniqueId;
    }
    const serverConfig = this.serverConfigService.getCollectorConfig();
    const collectorURL = this.lmapService.buildCollectorPostUrl(serverConfig, this.deviceModel);
    const testSchedule = this.createTestSchedule(testPointA, collectorURL, dataModel);
    const controllerConfig = this.createControllerConfig(testPointA, testPointB, testSchedule);
    if (!this.output.scheduler?.repeatInterval) {
      this.output[ConfigFieldMapping.RUN_TYPE] = 'on-demand';
    }
    controllerConfig.test['test-list-data']['runType'] = this.output.runType;
    return controllerConfig;
  }

  createTestSchedule(testPointA: any, collectorURL: string, dataModel: any): any {
    const tagOptions: Array<string> = ['monitor-type:on-going'];
    const schedObj: SchedulerFormat = { ...this.output[ConfigFieldMapping.SCHEDULER] };
    // create the test schedule
    const testScheduleName = this.output[ConfigFieldMapping.ALIAS];
    const testActionName = testScheduleName + '-action';
    let testEventName = testScheduleName + '-schedule-event';

    // For start immediate and repeat once test
    if (!schedObj.timeStamp && !schedObj.repeatInterval) {
      testEventName = 'immediate';
    }

    const allTestParams = {};
    allTestParams['rfc2544-viavi'] = dataModel;

    const actionOption = {
      id: this.testTaskName,
      value: JSON.stringify(allTestParams)
    };

    let optionArray = [];
    optionArray.push(actionOption);

    if (testPointA.phyId !== '') {
      const actionOptionPhy = {
        id: 'phy-id',
        value: testPointA.phyId
      };
      optionArray.push(actionOptionPhy);
    }
    optionArray = [...optionArray, ...this.getOptions()];

    const tags = this.output.scheduler?.repeatInterval ? tagOptions : null;
    const action = this.lmapService.createAction(
      testActionName,
      this.testTaskName,
      optionArray,
      null,
      tags
    );
    const actionArray = [];
    actionArray.push(action);
    // while report schedule references could be created here they will be added
    //automatically to this schedule via addResultScheduleAndEvent()
    const testSchedule = this.lmapService.createSchedule(
      testScheduleName,
      testEventName,
      actionArray
    );

    // build the LMAP configuration (test schedule + result schedules + all events other than those predefined by MA such as immediate)
    let lmapified = this.lmapService.addSchedule(testSchedule);
    this.lmapService.CONST.resultFormatType = 'v2';
    // final results are mandatory
    lmapified = this.lmapService.addResultScheduleAndEvent(
      lmapified,
      testScheduleName,
      testEventName,
      'immediate',
      'final',
      collectorURL
    );
    // rt results are technically optional, but mandatory for us
    // set to 1 second interval
    lmapified = this.lmapService.addResultScheduleAndEvent(
      lmapified,
      testScheduleName,
      testEventName,
      'periodic',
      'rt',
      collectorURL,
      schedObj.repeatInterval,
      schedObj.timeStamp,
      null
    );
    return lmapified;
  }

  getOptions(): any {
    let vlanConfig = [];
    if (this.output[ConfigFieldMapping.VLAN_CONFIG] === vlanConfigValue.Q_IN_Q) {
      vlanConfig = [
        {
          id: 'vlan-id',
          value: this.output[ConfigFieldMapping.SVLAN_ID].toString()
        },
        {
          id: 'vlan-priority',
          value: this.output[ConfigFieldMapping.SVLAN_PRIORITY].toString()
        },
        {
          id: 'inner-vlan-id',
          value: this.output[ConfigFieldMapping.CVLAN_ID].toString()
        },
        {
          id: 'inner-vlan-priority',
          value: this.output[ConfigFieldMapping.CVLAN_PRIORITY].toString()
        }
      ];
      if (this.output[ConfigFieldMapping.VERIFY_PRIORITY]) {
        vlanConfig.push({
          id: 'inner-filter-on-priority',
          value: this.output[ConfigFieldMapping.VERIFY_PRIORITY].toString()
        });
      }
    } else if (this.output[ConfigFieldMapping.VLAN_CONFIG] === vlanConfigValue.SINGLE_VLAN) {
      vlanConfig = [
        {
          id: 'vlan-id',
          value: this.output[ConfigFieldMapping.SVLAN_ID].toString()
        },
        {
          id: 'vlan-priority',
          value: this.output[ConfigFieldMapping.SVLAN_PRIORITY].toString()
        }
      ];
      if (this.output[ConfigFieldMapping.VERIFY_PRIORITY]) {
        vlanConfig.push({
          id: 'filter-on-priority',
          value: this.output[ConfigFieldMapping.VERIFY_PRIORITY].toString()
        });
      }
    }
    let options = [];
    if (this.output[ConfigFieldMapping.IP_ADDRESS_TYPE] === IPAddressValue.IP_V4) {
      options.push({
        id: 'source-ip',
        value: this.output[ConfigFieldMapping.SRC_IPV4_ADDRESS]
      });
    } else if (this.output[ConfigFieldMapping.IP_ADDRESS_TYPE] === IPAddressValue.IP_V6) {
      options.push({
        id: 'source-ip',
        value: this.output[ConfigFieldMapping.SRC_IPV6_ADDRESS]
      });
    }
    options = [...options, ...vlanConfig];
    if (this.output[ConfigFieldMapping.LAYER_TO_TEST] === TestLayerValue.L3) {
      options = [
        ...options,
        ...[
          {
            id: 'gateway',
            value: this.output[ConfigFieldMapping.SRC_GATEWAY]
          }
        ]
      ];
      if (this.output[ConfigFieldMapping.IP_ADDRESS_TYPE] === IPAddressValue.IP_V4) {
        options.push({
          id: 'subnet',
          value: this.output[ConfigFieldMapping.SRC_SUBNET]
        });
      } else if (this.output[ConfigFieldMapping.IP_ADDRESS_TYPE] === IPAddressValue.IP_V6) {
        options.push({
          id: 'prefix-length',
          value: this.output[ConfigFieldMapping.SRC_SUBNET]
        });
      }
    }
    if (this.output[ConfigFieldMapping.STREAM]['cir-from-engine-a']) {
      options = [
        ...options,
        ...[
          {
            id: 'subscribed-line-rate',
            value: this.output[ConfigFieldMapping.STREAM]['cir-from-engine-a'] * 1000
          }
        ]
      ];
    }
    if (
      this.output[ConfigFieldMapping.BACK_TO_BACK] ||
      this.output[ConfigFieldMapping.AGENT_TYPE] === AGENT_MODEL.QT600
    ) {
      options = [
        ...options,
        ...[
          {
            id: 'max-line-rate-for-test',
            value: this.output[ConfigFieldMapping.BACK_TO_BACK_MAX_LINE_RATE].toString()
          }
        ]
      ];
    }
    return options;
  }

  createControllerConfig(testPointA, testPointB, lmapified): any {
    const controllerConfig = this.testAssembleService.buildPrimaryTestConfig(
      testPointA,
      this.testTaskName,
      lmapified
    );
    controllerConfig.test['test-list-data'] = {};
    controllerConfig.test['test-list-data']['tpInfo'] = [
      {
        type: 'primary',
        id: testPointA.uniqueId,
        ip: testPointA.mgmtIPaddress
      }
    ];
    controllerConfig.test['extended-config'] = {};
    controllerConfig.test['test-list-data']['interval'] = this.output.scheduler?.repeatInterval;
    controllerConfig.test['test-list-data']['mode'] = this.output[ConfigFieldMapping.TEST_MODE];
    controllerConfig.test['test-list-data']['customer'] = {
      name: this.output[ConfigFieldMapping.CUSTOMER],
      contact: this.output.contact,
      email: this.output.email,
      phone: this.output.phone
    };
    controllerConfig.test['test-list-data']['layer'] =
      this.output[ConfigFieldMapping.LAYER_TO_TEST];
    controllerConfig.test['extended-config']['customerInfo'] = {
      name: this.output[ConfigFieldMapping.CUSTOMER],
      contact: this.output.contact,
      email: this.output.email,
      phone: this.output.phone
    };
    controllerConfig.test['extended-config']['technicianInfo'] = {
      name: this.output[ConfigFieldMapping.TECHINICIAN],
      id: this.output.technicianId
    };
    controllerConfig.test['extended-config']['testInfo'] = {
      name: this.output[ConfigFieldMapping.ALIAS],
      comments : this.output.comments
    };
    controllerConfig.test['extended-config']['tos-diffServ'] = {
      value: this.output[ConfigFieldMapping.STREAM]['tos-diff-serv']
    };
    controllerConfig.test['extended-config']['ipv6-stream-info'] = this.getIpv6StreamConfig();
    controllerConfig.test['extended-config']['test'] = {
      mode: this.output[ConfigFieldMapping.TEST_MODE],
      nameOnProbe: 'rfc2544',
      selected: true,
      testType: 'RFC2544'
    };
    controllerConfig.test['extended-config']['testPoints'] = [
      {
        tag: 'Test Point A',
        selected: true,
        type: 'inventory',
        id: testPointA.id,
        testPortPhyId: testPointA.phyId,
        name: testPointA.nameId,
        vendor: 'viavi',
        model: testPointA.model.model,
        version: testPointA.params.find(param => param.name === 'version').value,
        ip: testPointA.ipaddress,
        port: '443'
      }
    ];
    //Adding unmanaged device properites
    if (this.output[ConfigFieldMapping.TEST_MODE] === TestMode.LOOPBACK) {
      controllerConfig.test['test-list-data']['tpInfo'].push({
        id: testPointB?.id,
        ip: this.getDestinationIPAddress(testPointB),
        type: 'secondary',
        model: testPointB?.type === 'unmanaged' ? 'unmanaged' : testPointB?.model?.model
      });
      controllerConfig.test['extended-config']['testPoints'].push({
        tag: 'Test Point B',
        selected: true,
        type: 'inventory',
        id: testPointB?.id,
        name: testPointB?.name,
        vendor: 'viavi',
        model: testPointB?.type === 'unmanaged' ? 'unmanaged' : testPointB?.model?.model,
        version: testPointB?.params.find(param => param.name === 'version')?.value,
        ip: testPointB?.ipaddress,
        port: '443'
      });
    }
    controllerConfig.test['ipv6'] = 'no';
    return controllerConfig;
  }

  //eslint-disable-next-line
  getDestinationIPAddress(testpoint: any): string {
    if (testpoint?.ipaddress) {
      return testpoint.ipaddress;
    } else {
      if (this.output[ConfigFieldMapping.LAYER_TO_TEST] === TestLayerValue.L2) {
        return this.output[ConfigFieldMapping.DESTINATION_MAC];
      } else {
        return this.output[ConfigFieldMapping.IP_ADDRESS_TYPE] === IPAddressValue.IP_V4
          ? this.output[ConfigFieldMapping.DEST_IPV4_ADDRESS]
          : this.output[ConfigFieldMapping.DEST_IPV6_ADDRESS];
      }
    }
  }

  getIpv6StreamConfig(): any {
    const stream = {};
    (stream['flow-label'] = this.output[ConfigFieldMapping.STREAM]['flow-label'] || '1'),
      (stream['traffic-class'] = this.output[ConfigFieldMapping.STREAM]['traffic-class'] || '00'),
      (stream['hop-limit'] = this.output[ConfigFieldMapping.STREAM]['hop-limit'] || 64);
    return stream;
  }

  getFrameListDetails(): any {
    const frameSizeInfo = {};
    let selectedFrames = this.output[ConfigFieldMapping.STREAM]['frame-size-list'];
    selectedFrames = selectedFrames.split(',');
    frameSizeInfo['use-frame-size-64'] = 'false';
    frameSizeInfo['use-frame-size-128'] = 'false';
    frameSizeInfo['use-frame-size-256'] = 'false';
    frameSizeInfo['use-frame-size-512'] = 'false';
    frameSizeInfo['use-frame-size-1024'] = 'false';
    frameSizeInfo['use-frame-size-1280'] = 'false';
    frameSizeInfo['use-frame-size-1518'] = 'false';
    frameSizeInfo['use-frame-size-9000'] = 'false';
    for (let i = 0; i < selectedFrames.length; i++) {
      if (
        this.output[ConfigFieldMapping.IP_ADDRESS_TYPE] === IPAddressValue.IP_V4 &&
        (selectedFrames[i] === '64' || selectedFrames[i] === '82')
      ) {
        frameSizeInfo['use-frame-size-64'] = 'true';
      }
      if (selectedFrames[i] === '128') {
        frameSizeInfo['use-frame-size-128'] = 'true';
      }
      if (selectedFrames[i] === '256') {
        frameSizeInfo['use-frame-size-256'] = 'true';
      }
      if (selectedFrames[i] === '512') {
        frameSizeInfo['use-frame-size-512'] = 'true';
      }
      if (selectedFrames[i] === '1024') {
        frameSizeInfo['use-frame-size-1024'] = 'true';
      }
      if (selectedFrames[i] === '1280') {
        frameSizeInfo['use-frame-size-1280'] = 'true';
      }
      if (selectedFrames[i] === '1518') {
        frameSizeInfo['use-frame-size-1518'] = 'true';
      }
      if (selectedFrames[i] === '9000') {
        frameSizeInfo['use-frame-size-9000'] = 'true';
      }
      if (selectedFrames[i].indexOf('custom') === 0) {
        frameSizeInfo['custom-frame-size'] = selectedFrames[i].replace('custom:', '');
      }
    }
    return frameSizeInfo;
  }

  getLayer2Details(): any {
    let layerInfo = {};
    layerInfo['device-selection-method'] = 'MAC';
    layerInfo['destination-mac'] = this.output[ConfigFieldMapping.DESTINATION_MAC];
    layerInfo['tos-or-dscp'] = this.output[ConfigFieldMapping.STREAM].dscp?.toString();
    if (
      this.output[ConfigFieldMapping.LAYER_TO_TEST] === TestLayerValue.L2 &&
      this.output[ConfigFieldMapping.IP_ADDRESS_TYPE] === IPAddressValue.IP_V4
    ) {
      layerInfo['destination-ip'] = this.output[ConfigFieldMapping.DEST_IPV4_ADDRESS];
    } else if (
      this.output[ConfigFieldMapping.LAYER_TO_TEST] === TestLayerValue.L2 &&
      this.output[ConfigFieldMapping.IP_ADDRESS_TYPE] === IPAddressValue.IP_V6
    ) {
      layerInfo['destination-ip'] = this.output[ConfigFieldMapping.DEST_IPV6_ADDRESS];
    }
    return layerInfo;
  }

  getLayer3Details(): any {
    let layerInfo = {};
    if (
      this.output[ConfigFieldMapping.LAYER_TO_TEST] === TestLayerValue.L3 &&
      this.output[ConfigFieldMapping.IP_ADDRESS_TYPE] === IPAddressValue.IP_V4
    ) {
      layerInfo['tos-or-dscp'] = this.output[ConfigFieldMapping.STREAM].dscp?.toString();
      layerInfo['destination-ip'] = this.output[ConfigFieldMapping.DEST_IPV4_ADDRESS];
      layerInfo['filter-on-tos'] =
        this.output[ConfigFieldMapping.STREAM]['tos-diff-serv'].toString();
    } else if (
      this.output[ConfigFieldMapping.LAYER_TO_TEST] === TestLayerValue.L3 &&
      this.output[ConfigFieldMapping.IP_ADDRESS_TYPE] === IPAddressValue.IP_V6
    ) {
      layerInfo['destination-ip'] = this.output[ConfigFieldMapping.DEST_IPV6_ADDRESS];
      layerInfo['traffic-class'] = this.output[ConfigFieldMapping.STREAM]['traffic-class'];
      layerInfo['flow-label'] = this.output[ConfigFieldMapping.STREAM]['flow-label'];
      layerInfo['hop-limit'] = this.output[ConfigFieldMapping.STREAM]['hop-limit'];
    }
    return layerInfo;
  }

  getThroughputDetails(): any {
    let throughputInfo = {};
    throughputInfo['show-pass-fail'] = this.output[ConfigFieldMapping.THROUGHPUT].toString();
    throughputInfo['stop-on-throughput-fail'] =
      this.output[ConfigFieldMapping.THROUGHTPUT_STOP_OVERALL_TEST_ON_FAILURE].toString();
    throughputInfo['duration'] = this.output[ConfigFieldMapping.THROUGHPUT_DURATION].toString();
    throughputInfo['bw-accuracy'] = this.output[ConfigFieldMapping.THROUGHPUT_BANDWIDTH].toString();
    throughputInfo['max-frame-loss'] =
      this.output[ConfigFieldMapping.THROUGHPUT_FRAME_LOSS_TOLERANCE].toString();
    throughputInfo['start-utilization'] =
      this.output[ConfigFieldMapping.THROUGHPUT_STARTING_UTILIZATION].toString();
    throughputInfo['success-utilization-high'] =
      this.output[ConfigFieldMapping.THROUGHPUT_MAX_UTILIZATION].toString();
    throughputInfo['success-utilization-low'] =
      this.output[ConfigFieldMapping.THROUGHPUT_MIN_UTILIZATION].toString();
    return throughputInfo;
  }

  getLatencyDetails(): any {
    let latencyInfo = {};
    latencyInfo['latency-show-pass-fail'] = this.output[ConfigFieldMapping.LATENCY].toString();
    latencyInfo['trials'] = this.output[ConfigFieldMapping.LATENCY_NUM_OF_TRIALS].toString();
    latencyInfo['fdv-show-pass-fail'] = this.output[ConfigFieldMapping.LATENCY].toString();
    latencyInfo['duration'] = this.output[ConfigFieldMapping.LATENCY_DURATION].toString();
    latencyInfo['latency-max'] = this.output[ConfigFieldMapping.TARGET_LATENCY].toString();
    latencyInfo['fdv-max'] = this.output[ConfigFieldMapping.TARGET_FDV].toString();
    latencyInfo['latency-utilization-percent'] =
      this.output[ConfigFieldMapping.LATENCY_UTILIZATION].toString();
    return latencyInfo;
  }

  getFrameLossDetails(): any {
    let frameLossInfo = {};
    frameLossInfo['show-pass-fail'] = this.output[ConfigFieldMapping.FRAME_LOSS].toString();
    frameLossInfo['skip-if-throughput-pass'] =
      this.output[ConfigFieldMapping.FRAME_LOSS_RUN_THORUGHPUT_FAILS].toString();
    frameLossInfo['duration'] = this.output[ConfigFieldMapping.FRAME_LOSS_DURATION].toString();
    frameLossInfo['max-percent'] =
      this.output[ConfigFieldMapping.FRAME_LOSS_MAX_LOSS_ALLOWED].toString();
    frameLossInfo['utilization-granularity'] =
      this.output[ConfigFieldMapping.FRAME_LOSS_REDUCE_UTILIZATION].toString();
    return frameLossInfo;
  }

  getB2bOrCbsDetails(): any {
    let b2bOrCbsInfo = {};
    b2bOrCbsInfo['show-pass-fail'] = this.output[ConfigFieldMapping.BACK_TO_BACK].toString();
    b2bOrCbsInfo['skip-if-throughput-pass'] = 'false';
    if (this.output[ConfigFieldMapping.BACK_TO_BACK_TEST_TYPE] === 'b2b') {
      let bbInfo = {};
      bbInfo['trials'] = this.output[ConfigFieldMapping.BACK_TO_BACK_REPETITIONS].toString();
      bbInfo['burst-duration'] =
        this.output[ConfigFieldMapping.BACK_TO_BACK_TARGET_BURST_DURATION].toString();
      bbInfo['receive-time'] =
        this.output[ConfigFieldMapping.BACK_TO_BACK_MAX_TIME_RECEIVE_BURST].toString();
      bbInfo['granularity'] =
        this.output[ConfigFieldMapping.BACK_TO_BACK_BURST_RESOLUTION].toString();
      b2bOrCbsInfo['bb'] = bbInfo;
    } else if (this.output[ConfigFieldMapping.BACK_TO_BACK_TEST_TYPE] === 'cbs') {
      let cbsInfo = {};
      cbsInfo['trials'] = this.output[ConfigFieldMapping.BACK_TO_BACK_REPETITIONS].toString();
      cbsInfo['burst-size'] = this.output[ConfigFieldMapping.CBS_COMMITTED_BURST_SIZE].toString();
      cbsInfo['burst-size-multiplier'] =
        this.output[ConfigFieldMapping.CBS_KILO_BYTE_SIZE].toString();
      b2bOrCbsInfo['cbs'] = cbsInfo;
    }
    return b2bOrCbsInfo;
  }

  //------------------------------------------------ Wizard control ----------------------------------------------------

  toggleTabChange(index: number): void {
    super.toggleTabChange(index);
    this.tabDataChangeEvents.next(index);
  }

  setOriginatorAgent(device: Device): void {
    this.selectedOriginator = device;
    this.agentChangeEvents.next(device);
  }
  getOriginatorAgent(): any {
    return this.selectedOriginator;
  }

  setDestinationAgent(device: Device): void {
    this.selectedDestination = device;
  }
}
